// History of acceptance-test reports — list, re-print, edit, delete.
// Each row stores the full snapshot (params + meta + serials + results),
// so re-render is byte-identical without re-running the generator.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { request } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const ACCESS_KEY = "mes:auth:access";

interface ReportRow {
  id: string;
  doc_number: string;
  article: string;
  params: Record<string, unknown>;
  meta: Record<string, unknown>;
  serials: string[];
  results: unknown[];
  summary: { total: number; passed: number; failed: number };
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface Page<T> {
  items: T[];
  pagination: { page: number; page_size: number; total: number; pages: number };
}

export default function TestReportHistory() {
  return (
    <RequirePermission codes="document.test_report.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");

  const list = useQuery({
    queryKey: ["admin", "test-reports", "list", page, q],
    queryFn: () =>
      request<Page<ReportRow>>(
        `/admin/test-reports?page=${page}&page_size=50${q ? `&q=${encodeURIComponent(q)}` : ""}`,
      ),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/admin/test-reports/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "test-reports"] }),
  });

  async function reprint(id: string) {
    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem(ACCESS_KEY) : null;
    const res = await fetch(`/api/v1/admin/test-reports/${id}/render`, {
      method: "POST",
      headers: { ...(token ? { authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      alert(t("testReportHistory.httpError", { status: res.status }));
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={t("testReportHistory.title")}
        description={t("testReportHistory.description")}
        actions={
          <Link to="/admin/print/test-reports" className="btn btn-primary">
            {t("testReportHistory.newReport")}
          </Link>
        }
      />

      <div className="card p-3 mt-3 flex items-center gap-2">
        <input
          className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-sm"
          placeholder={t("testReportHistory.searchPlaceholder")}
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
      </div>

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}

      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">
          {t("testReportHistory.empty")}
        </div>
      )}

      {list.data && list.data.items.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("testReportHistory.colDocNumber")}</th>
                <th className="px-3 py-2 text-left">{t("testReportHistory.colArticle")}</th>
                <th className="px-3 py-2 text-right">{t("testReportHistory.colSerials")}</th>
                <th className="px-3 py-2 text-right">{t("testReportHistory.colPassed")}</th>
                <th className="px-3 py-2 text-left">{t("common.created")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{r.doc_number}</td>
                  <td className="px-3 py-2">{r.article}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {r.serials.length}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`pill ${
                        r.summary.failed === 0 ? "pill-ok" : "pill-warn"
                      } text-xs`}
                    >
                      {r.summary.passed} / {r.summary.total}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-text-muted text-xs font-mono">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button className="btn btn-sm" onClick={() => reprint(r.id)}>
                      {t("testReportHistory.print")}
                    </button>{" "}
                    <Link
                      to={`/admin/print/test-reports?from=${r.id}`}
                      className="btn btn-sm"
                    >
                      {t("testReportHistory.editAction")}
                    </Link>{" "}
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        if (confirm(t("testReportHistory.confirmDelete", { doc: r.doc_number }))) {
                          del.mutate(r.id);
                        }
                      }}
                      disabled={del.isPending}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {del.isError && (
        <ErrorBanner onDismiss={() => del.reset()}>
          {(del.error as Error).message}
        </ErrorBanner>
      )}

      {list.data && list.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", { page: list.data.pagination.page, pages: list.data.pagination.pages, total: list.data.pagination.total })}
          </span>
          <div className="flex gap-2">
            <button
              className="btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ←
            </button>
            <button
              className="btn"
              disabled={page >= list.data.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}
